/**
 * FairnessLauncher — 4-step wizard to launch a fairness evaluation.
 * Phase 4.10 β5 — WQ-240.
 *
 * Steps:
 *   1. Pick a completed experiment (best-run-bearing)
 *   2. Define a protected attribute (name + comma-separated values per row)
 *   3. Confirm problem type (classification or regression)
 *   4. Review & Launch
 *
 * Wraps existing POST /api/admin/ai/fairness/evaluate. Backend evaluates
 * fairness on the experiment's best-run metrics with operator-supplied
 * attribute values.
 *
 * Pattern reuse: WQ-237 AutoMLWizard + WQ-238 PredictionRequestForm
 * (copy-paste-with-divergence per WQ-237 NOTES).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  runFairnessEvaluation,
  listFairnessExperiments,
  type FairnessRunResponse,
} from '../../services/fairnessService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3 | 4;
type ProblemType = 'classification' | 'regression';

interface FairnessLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (experimentId: string) => void;
}

interface ExperimentOption {
  id: string;
  name: string;
  status: string;
  problem_type: string;
  best_run_id: string | null;
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Choose an experiment',
  2: 'Define a protected attribute',
  3: 'Confirm problem type',
  4: 'Review & launch',
};

function parseValues(raw: string): Array<string | number> {
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => {
      const n = Number(v);
      return Number.isFinite(n) && v.match(/^-?\d+(\.\d+)?$/) ? n : v;
    });
}

const FairnessLauncher: React.FC<FairnessLauncherProps> = ({ isOpen, onClose, onSubmitted }) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 state
  const [experiments, setExperiments] = useState<ExperimentOption[]>([]);
  const [experimentsLoading, setExperimentsLoading] = useState(false);
  const [experimentsError, setExperimentsError] = useState<string | null>(null);
  const [selectedExperiment, setSelectedExperiment] = useState<ExperimentOption | null>(null);

  // Step 2 state
  const [attributeName, setAttributeName] = useState<string>('');
  const [attributeValuesRaw, setAttributeValuesRaw] = useState<string>('');

  // Step 3 state
  const [problemType, setProblemType] = useState<ProblemType>('classification');

  // Submit state
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset wizard each time it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedExperiment(null);
      setAttributeName('');
      setAttributeValuesRaw('');
      setProblemType('classification');
      setLaunchError(null);
    }
  }, [isOpen]);

  // Load experiments when wizard opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setExperimentsLoading(true);
      setExperimentsError(null);
      try {
        const data = await listFairnessExperiments(authFetch);
        if (cancelled) return;
        setExperiments(data);
      } catch (err) {
        if (!cancelled) setExperimentsError(err instanceof Error ? err.message : 'Failed to load experiments');
      } finally {
        if (!cancelled) setExperimentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authFetch]);

  // Auto-fill problem type when an experiment is selected
  useEffect(() => {
    if (selectedExperiment && (selectedExperiment.problem_type === 'regression' || selectedExperiment.problem_type === 'classification')) {
      setProblemType(selectedExperiment.problem_type);
    }
  }, [selectedExperiment]);

  const parsedValues = useMemo(() => parseValues(attributeValuesRaw), [attributeValuesRaw]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!selectedExperiment;
    if (step === 2) return attributeName.trim().length > 0 && parsedValues.length >= 2;
    if (step === 3) return !!problemType;
    return false;
  }, [step, selectedExperiment, attributeName, parsedValues.length, problemType]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 4 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const handleLaunch = async () => {
    if (!selectedExperiment) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const result: FairnessRunResponse = await runFairnessEvaluation(authFetch, {
        experiment_id: selectedExperiment.id,
        protected_attributes: { [attributeName.trim()]: parsedValues },
        problem_type: problemType,
      });
      if (onSubmitted) onSubmitted(result.experiment_id);
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to launch fairness evaluation');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Run Fairness Evaluation"
      totalSteps={4}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="fairness-launcher"
      canAdvance={canAdvance}
      submitting={launching}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleLaunch}
      launchError={launchError}
      maxWidthPx={680}
    >
      <>
          {step === 1 && (
            <div data-testid="fairness-launcher-step-1">
              {experimentsLoading && (
                <div data-testid="fairness-launcher-experiments-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading experiments...
                </div>
              )}
              {experimentsError && (
                <div data-testid="fairness-launcher-experiments-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 12,
                }}>
                  {experimentsError}
                </div>
              )}
              {!experimentsLoading && !experimentsError && experiments.length === 0 && (
                <div data-testid="fairness-launcher-empty-experiments" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                  border: '1px dashed var(--border)', borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>No completed experiments available</div>
                  <div style={{ fontSize: 13 }}>
                    Run an AutoML sweep first; fairness evaluation requires a best-run with stored metrics.
                  </div>
                </div>
              )}
              {experiments.length > 0 && (
                <div data-testid="fairness-launcher-experiment-list">
                  {experiments.map((e) => (
                    <button
                      key={e.id}
                      data-testid={`fairness-launcher-experiment-${e.id}`}
                      onClick={() => setSelectedExperiment(e)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                        border: selectedExperiment?.id === e.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                        background: selectedExperiment?.id === e.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{e.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                        ({e.problem_type})
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="fairness-launcher-step-2">
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Attribute name *
                </label>
                <input
                  type="text"
                  data-testid="fairness-launcher-attr-name"
                  value={attributeName}
                  onChange={(e) => setAttributeName(e.target.value)}
                  placeholder="gender, race, age_group, ..."
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Values per row (comma-separated) *
                </label>
                <textarea
                  data-testid="fairness-launcher-attr-values"
                  value={attributeValuesRaw}
                  onChange={(e) => setAttributeValuesRaw(e.target.value)}
                  placeholder="M, F, M, F, M, F, ..."
                  rows={5}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                  }}
                />
                <div data-testid="fairness-launcher-values-count" style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {parsedValues.length} value{parsedValues.length === 1 ? '' : 's'} parsed
                  {parsedValues.length < 2 && attributeValuesRaw.length > 0 && (
                    <span style={{ color: '#dc2626', marginLeft: 8 }}>(at least 2 required)</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="fairness-launcher-step-3">
              <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                Pre-filled from the selected experiment. Override if needed.
              </div>
              <div role="radiogroup" data-testid="fairness-launcher-problem-type-group">
                {(['classification', 'regression'] as ProblemType[]).map((pt) => (
                  <label
                    key={pt}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      border: problemType === pt ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                      background: problemType === pt ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                    }}
                  >
                    <input
                      type="radio"
                      name="fairness-launcher-problem-type"
                      data-testid={`fairness-launcher-problem-type-${pt}`}
                      value={pt}
                      checked={problemType === pt}
                      onChange={() => setProblemType(pt)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{pt}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && selectedExperiment && (
            <div data-testid="fairness-launcher-step-4">
              <div style={{
                padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
              }}>
                <div data-testid="fairness-launcher-review-summary">
                  Evaluate fairness on experiment <strong>{selectedExperiment.name}</strong>.
                  <br />
                  Protected attribute: <code>{attributeName.trim()}</code> ({parsedValues.length} value{parsedValues.length === 1 ? '' : 's'}).
                  <br />
                  Problem type: <strong>{problemType}</strong>.
                </div>
              </div>
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

export default FairnessLauncher;
